import React from 'react';
import { isDebugMode } from '../lib/debug';

/** Safely coerce error or error.message to a string (avoids "Cannot convert object to primitive value"). */
function toErrorDisplayString(error) {
  if (error == null) return 'An unexpected error occurred';
  const msg = error?.message ?? error;
  if (typeof msg === 'string') return msg;
  if (msg && typeof msg === 'object' && Array.isArray(msg.errors)) return msg.errors.flat().join(' ');
  if (msg && typeof msg === 'object' && msg.errors && typeof msg.errors === 'object') return Object.values(msg.errors).flat().join(' ');
  try { return String(msg); } catch { return 'An unexpected error occurred'; }
}

/** Safely get a string for stack trace display. */
function toStackString(error) {
  if (!error) return '';
  const s = error.stack;
  return typeof s === 'string' ? s : '';
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (isDebugMode) {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      const displayMessage = isDebugMode ? toErrorDisplayString(this.state.error) : 'An unexpected error occurred. Please try reloading the page.';
      const stackStr = toStackString(this.state.error);

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4" style={{ backgroundColor: 'transparent' }}>
          <div className="text-center max-w-md border border-border rounded-lg p-8">
            <h1 className="text-2xl font-bold mb-4 text-destructive">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">
              {displayMessage}
            </p>
            {isDebugMode && stackStr && (
              <details className="text-left mt-4 p-4 bg-muted rounded-lg text-xs font-mono overflow-auto max-h-48">
                <summary className="cursor-pointer mb-2 font-sans">Error Details</summary>
                <pre className="whitespace-pre-wrap">{stackStr}</pre>
              </details>
            )}
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;




